import React, { useEffect, useState } from "react";
import "./users.css";
import { Form } from "react-bootstrap";
import { AiOutlineArrowLeft, AiOutlineCheck } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { inviteUser, portalsDropDown } from "../../redux/actions/userAction";
import errorToast from "../../components/toast/errorToast";

const STEPS = [
  { label: "User Type" },
  { label: "Add Method" },
  { label: "User Details" },
];

const InviteUsers = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [signinMethod, setSigninMethod] = useState("");
  const [portalList, setPortalList] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [userType, setUserType] = useState("");
  const [pageLimit, setPageLimit] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Invite User - Acumen Translation Hub";
  }, []);

  useEffect(() => {
    dispatch(portalsDropDown(onPortalDropDownSuccess, onError));
  }, []);

  const onPortalDropDownSuccess = (data) => {
    setPortalList(
      data.data.data.map((item) => ({ label: item.portal_name, value: item.id }))
    );
  };

  const onSuccess = () => {
    navigate("/users");
  };

  const onError = (data) => {
    setSubmitting(false);
    errorToast(data?.data?.message || "Something went wrong");
  };

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  const handleNext = () => {
    if (activeStep === 0 && !userType) {
      errorToast("Please select a user type to continue.");
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleGoBack = () => navigate(-1);

  const handlePortalSelect = (e) => {
    const { value, checked } = e.target;
    setSelectedPortals((prev) =>
      checked ? [...prev, value] : prev.filter((p) => p !== value)
    );
  };

  const handleSubmit = () => {
    if (!userEmail || !isValidEmail(userEmail)) {
      errorToast("Please enter a valid email address.");
      return;
    }
    if (!signinMethod) {
      errorToast("Please select a sign-in method.");
      return;
    }
    setSubmitting(true);
    dispatch(
      inviteUser(
        {
          email: userEmail,
          sign_in: signinMethod,
          user_type: userType,
          page_limit: pageLimit ? Number(pageLimit) : 0,
          portals: selectedPortals,
        },
        onSuccess,
        onError
      )
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="text-start">
            <h5 className="fw_600 mb-1">Select user type</h5>
            <p className="fs_13 text-muted mb-3">
              Choose the role for the user you're inviting.
            </p>
            <div className="invite-type-cards">
              <div
                className={`invite-type-card${userType === "portal" ? " selected" : ""}`}
                onClick={() => setUserType("portal")}
              >
                <input
                  type="radio"
                  name="userType"
                  value="portal"
                  checked={userType === "portal"}
                  onChange={() => setUserType("portal")}
                  className="mt-1 flex-shrink-0"
                />
                <div>
                  <p className="fw_600 fs_14 mb-1" style={{ color: "#212529" }}>
                    Portal User
                  </p>
                  <p className="fs_13 text-muted mb-0">
                    Can initiate translation jobs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="text-start">
            <h5 className="fw_600 mb-1">Select method to add users</h5>
            <p className="fs_13 text-muted mb-3">
              Choose how you'd like to add this user.
            </p>
            <div className="invite-type-cards">
              <div className="invite-type-card selected" style={{ cursor: "default" }}>
                <div className="invite-method-check">
                  <AiOutlineCheck style={{ fontSize: 13 }} />
                </div>
                <div>
                  <p className="fw_600 fs_14 mb-1" style={{ color: "#212529" }}>
                    Add manually
                  </p>
                  <p className="fs_13 text-muted mb-0">
                    Add a single user by entering their email address and assigning portals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="text-start">
            <h5 className="fw_600 mb-1">User details</h5>
            <p className="fs_13 text-muted mb-3">
              Enter the user's information and assign portals.
            </p>
            <Form>
              <Form.Group className="mb-3" controlId="inviteEmail">
                <Form.Label className="fs_13 fw_600">
                  Email address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="user@example.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="inviteSignin">
                <Form.Label className="fs_13 fw_600">
                  Sign-in method <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={signinMethod}
                  onChange={(e) => setSigninMethod(e.target.value)}
                >
                  <option value="">Select sign-in method</option>
                  <option value="google">Sign in with Google</option>
                  <option value="email">Email and Password</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="invitePageLimit">
                <Form.Label className="fs_13 fw_600">Page limit</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="Enter page limit (optional)"
                  value={pageLimit}
                  onChange={(e) => setPageLimit(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="invitePortals">
                <Form.Label className="fs_13 fw_600">Assign portals</Form.Label>
                {portalList.length === 0 ? (
                  <p className="fs_13 text-muted mt-1">No portals available.</p>
                ) : (
                  <div className="invite-portal-list">
                    {portalList.map((portal) => (
                      <Form.Check
                        key={portal.value}
                        type="checkbox"
                        id={`portal-${portal.value}`}
                        label={portal.label}
                        value={portal.value}
                        checked={selectedPortals.includes(portal.value)}
                        onChange={handlePortalSelect}
                        disabled={!signinMethod || !userEmail}
                        className="fs_13"
                      />
                    ))}
                  </div>
                )}
              </Form.Group>
            </Form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-100 h-100">
      {/* Page header */}
      <div
        className="d-flex border-bottom px-3 align-items-center position-sticky top-0 bg-white z-3"
        style={{ height: 53 }}
      >
        <AiOutlineArrowLeft
          className="cursor_pointer me-2"
          style={{ color: "#4A81E0", fontSize: 20 }}
          onClick={handleGoBack}
        />
        <span className="fs_18 fw_500">Invite Users</span>
      </div>

      {/* Stepper + content */}
      <div className="invite-stepper-wrap">
        {/* Horizontal step indicators */}
        <div className="invite-stepper">
          {STEPS.map((step, index) => {
            const state =
              index < activeStep
                ? "completed"
                : index === activeStep
                  ? "active"
                  : "upcoming";
            return (
              <div
                key={step.label}
                className={`invite-step-item ${state}`}
                onClick={() => index < activeStep && setActiveStep(index)}
                style={{ cursor: index < activeStep ? "pointer" : "default" }}
              >
                <div className="invite-step-circle">
                  {state === "completed" ? <AiOutlineCheck /> : index + 1}
                </div>
                <span className="invite-step-label">{step.label}</span>
              </div>
            );
          })}
        </div>

        {/* Active step content */}
        <div className="invite-step-body">
          {getStepContent(activeStep)}

          <div className="mt-4 d-flex gap-3">
            {activeStep > 0 && (
              <button
                className="btn btn-outline-secondary"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </button>
            )}
            {activeStep < STEPS.length - 1 && (
              <button className="btn btn-primary" onClick={handleNext}>
                Continue
              </button>
            )}
            {activeStep === STEPS.length - 1 && (
              <button
                className="btn btn-success d-flex align-items-center gap-2"
                onClick={handleSubmit}
                disabled={!userEmail || !isValidEmail(userEmail) || submitting}

              >
                {submitting && (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                )}
                {submitting ? "Sending..." : "Send Invite"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteUsers;
